/**
 * UK Date/Time Formatting Utilities
 * 
 * All dates display as dd/mm/yyyy
 * All times display as 24-hour format
 * 
 * Database storage remains ISO format - these are display-only functions
 */

// Format date as dd/mm/yyyy
export const formatDateGB = (dateInput) => {
  if (!dateInput) return '-';
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch {
    return '-';
  }
};

// Format date as "8 Feb 2026" or similar short format
export const formatDateShort = (dateInput) => {
  if (!dateInput) return '-';
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch {
    return '-';
  }
};

// Format date as "Sunday 8 February 2026"
export const formatDateLong = (dateInput) => {
  if (!dateInput) return '-';
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch {
    return '-';
  }
};

// Format time as 24-hour HH:mm
export const formatTime24 = (timeInput) => {
  if (!timeInput) return '-';
  try {
    // Handle time strings like "14:30"
    if (typeof timeInput === 'string' && timeInput.match(/^\d{1,2}:\d{2}/)) {
      const [hours, minutes] = timeInput.split(':');
      return `${hours.padStart(2, '0')}:${minutes}`;
    }
    // Handle Date objects
    const date = typeof timeInput === 'string' ? new Date(timeInput) : timeInput;
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  } catch {
    return '-';
  }
};

// Format datetime as "08/02/2026 14:30"
export const formatDateTimeGB = (dateInput) => {
  if (!dateInput) return '-';
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '-';
    const dateStr = date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
    return `${dateStr} ${timeStr}`;
  } catch {
    return '-';
  }
};

// Format relative time like "Today", "Yesterday", "8 Feb"
export const formatDateRelative = (dateInput) => {
  if (!dateInput) return '-';
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '-';
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const inputDate = new Date(date);
    inputDate.setHours(0, 0, 0, 0);
    
    const diffDays = Math.floor((today - inputDate) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays === -1) return 'Tomorrow';
    
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch {
    return '-';
  }
};

// Format month and year like "February 2026"
export const formatMonthYear = (dateInput) => {
  if (!dateInput) return '-';
  try {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  } catch {
    return '-';
  }
};

// FullCalendar locale settings for UK format
export const fullCalendarLocale = {
  locale: 'en-gb',
  firstDay: 1, // Monday
  eventTimeFormat: {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  },
  slotLabelFormat: {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  },
  titleFormat: {
    month: 'long',
    year: 'numeric'
  },
  dayHeaderFormat: {
    weekday: 'short',
    day: 'numeric'
  }
};
